import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Paper,
  Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import CustomPagination from '../../components/forms/Pagination';
import useRecipeV2Api from '../RecipeV2/hooks/useRecipeV2Api';
import { RecipeComplianceMatrix as Matrix } from '../../app/services/api/recipeV2';
import { RecipeV2ClientMocked } from '../../app/services/api/recipeV2';
import { isDemoModeEnabled } from '../../utils/featureAccess';

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  const normalized = status.toLowerCase();
  if (normalized === 'contains' || normalized === 'notsuitable') return 'error';
  if (normalized === 'maycontain') return 'warning';
  if (normalized === 'suitable' || normalized === 'certified' || normalized === 'freefrom') return 'success';
  return 'default';
};

const RecipeComplianceMatrixPage = () => {
  const { t } = useTranslation('recipeV2');
  const governedRecipeApi = useRecipeV2Api();
  const demoRecipeApi = useMemo(() => new RecipeV2ClientMocked(), []);
  // lazy: the matrix uses retained mock facts only while Demo Mode is active; replace with
  // governed API facts when the demo catalogue and published recipes are reconciled.
  const recipeApi = isDemoModeEnabled() ? demoRecipeApi : governedRecipeApi;
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(0);
  const [matrix, setMatrix] = useState<Matrix>({ columns: [], recipes: [], totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const rowsPerPage = 25;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    recipeApi.getRecipeComplianceMatrix(search, category, page + 1, rowsPerPage)
      .then((result) => { if (!cancelled) setMatrix(result); })
      .catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : t('complianceMatrix.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [recipeApi, search, category, page, t]);

  return (
    <PageArea>
      <PageTitle subTitle={t('complianceMatrix.subtitle')}>
        {t('complianceMatrix.title')}
      </PageTitle>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mt: 3, mb: 2 }}>
        <TextField
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(0); }}
          label={t('complianceMatrix.search')}
          placeholder={t('complianceMatrix.searchPlaceholder')}
          size="small"
          sx={{ minWidth: 280 }}
        />
        <FormControl
          size="small"
          sx={{ minWidth: 220 }}>
          <InputLabel>{t('complianceMatrix.category')}</InputLabel>
          <Select
            value={category}
            label={t('complianceMatrix.category')}
            onChange={(event) => { setCategory(event.target.value); setPage(0); }}>
            <MenuItem value="">{t('complianceMatrix.allCategories')}</MenuItem>
            <MenuItem value="Allergen">{t('dietary.categories.allergen')}</MenuItem>
            <MenuItem value="Intolerance">{t('dietary.categories.intolerance')}</MenuItem>
            <MenuItem value="DietaryRequirement">{t('dietary.categories.dietaryRequirement')}</MenuItem>
            <MenuItem value="LifestylePreference">{t('dietary.categories.lifestylePreference')}</MenuItem>
            <MenuItem value="NutritionalClaim">{t('dietary.categories.nutritionalClaim')}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Typography
        variant="textSM"
        color="text.secondary"
        sx={{ mb: 2, display: 'block' }}>
        {t('complianceMatrix.legend')}
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table
            stickyHeader
            size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 240, position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'background.paper' }}>
                  {t('complianceMatrix.recipe')}
                </TableCell>
                {matrix.columns.map((column) => (
                  <TableCell
                    key={column.code}
                    align="center"
                    sx={{ minWidth: 90, maxWidth: 110 }}>
                    <Tooltip title={column.name}>
                      <Typography
                        variant="textXS"
                        fontWeight={600}
                        sx={{ whiteSpace: 'normal' }}>
                        {column.name}
                      </Typography>
                    </Tooltip>
                    <Typography
                      variant="textXS"
                      color="text.secondary"
                      display="block">
                      {column.category}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell
                    colSpan={Math.max(matrix.columns.length + 1, 1)}
                    align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && matrix.recipes.map((recipe) => (
                <TableRow
                  key={recipe.recipeId}
                  hover>
                  <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                    <Typography
                      variant="textSM"
                      fontWeight={500}>
                      {recipe.recipeName}
                    </Typography>
                  </TableCell>
                  {matrix.columns.map((column) => {
                    const cell = recipe.cells[column.code];
                    const label = cell?.status || 'Unknown';
                    const displayStatus = label.toLowerCase() === 'freefrom'
                      ? t('dietary.freeFrom')
                      : t(`complianceMatrix.status.${label.toLowerCase()}`, label);
                    return (
                      <TableCell
                        key={column.code}
                        align="center">
                        <Chip
                          size="small"
                          label={displayStatus}
                          color={statusColor(label)}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {!loading && !matrix.recipes.length && (
                <TableRow>
                  <TableCell
                    colSpan={Math.max(matrix.columns.length + 1, 1)}
                    align="center">
                    {t('complianceMatrix.empty')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <CustomPagination
          page={page}
          setPage={setPage}
          maxPages={Math.ceil(matrix.totalCount / rowsPerPage)}
          breakpointForChangeDisplay={700}
          disabled={loading}
        />
      </Box>
    </PageArea>
  );
};

export default RecipeComplianceMatrixPage;
